import React, { useCallback, useEffect, useRef } from 'react';
import classNames from 'classnames';
import { BLUR_CONTENT_STYLE_NAME } from 'theme';
import { AutoScroll } from 'components/blocks/AutoScroll';
import Grid from 'components/common/Grid/Grid';
import { IGridProps } from 'components/common/Grid/types';
import { useOTSTranslation } from 'i18n/useOTSTranslation';
import { useVisibilityWindow } from 'util/useVisibilityWindow';

import styles from './styles.module.scss';

interface IProps extends IGridProps {
  loadNextPage: () => void,
  setIsLoading: (value: boolean) => void,
  isLoadMoreAvailable?: boolean,
  isLoading?: boolean,
  isFresh?: boolean,
}

export const GridLoadable = ({
  isFresh,
  loadNextPage,
  isLoading,
  setIsLoading,
  isLoadMoreAvailable,
  ...props
} : IProps) => {
  const { t } = useOTSTranslation('common');
  const buttonRef = useRef<HTMLButtonElement>(null);
  const buttonVisible = useVisibilityWindow(buttonRef, false, true, 0);

  const loadMoreAutomatically = useCallback(() => {
    if (buttonVisible && !isLoading) {
      if (buttonRef.current) {
        const pos = buttonRef.current.getBoundingClientRect();
        const topEdgeIn = pos.y > 0 && pos.y < window.innerHeight;
        const bottomEdgeIn = pos.y + pos.height > 0 && pos.y + pos.height < window.innerHeight;
        const isVisible = topEdgeIn || bottomEdgeIn;

        if (isVisible) {
          setIsLoading(true);
          setTimeout(() => {
            loadNextPage();
          }, 10);
        }
      }
    }
  }, [isLoading, buttonVisible, loadNextPage, setIsLoading]);

  useEffect(() => {
    loadMoreAutomatically();
  }, [isLoading, buttonVisible, loadNextPage, setIsLoading]);

  return (
    <>
      <div className={classNames(isLoading ? BLUR_CONTENT_STYLE_NAME : '')}>
        <AutoScroll>
          <Grid
            {...props}
          />
        </AutoScroll>
      </div>
      {!isLoading && isLoadMoreAvailable && (
        <button
          ref={buttonRef}
          type="button"
          className={styles.loadMore}
          onClick={loadNextPage}
        >
          {t('common.loadMore')}
        </button>
      )}
    </>
  );
};
